'use client'

import { useEffect, useState } from 'react'
import kountSDK from '@kount/kount-web-client-sdk'

import { useFraudStore } from '@rewardops/ui'
import { Logger } from '@rewardops/utils'

interface Kount360Props {
  clientId: string
  environment: 'TEST' | 'PROD'
}

/**
 * Generates a unique session ID for Kount 360 Device Data Collector
 * Must be 32 chars max, alphanumeric/dashes/underscores, and unique for 30 days
 */
const generateSessionId = (): string => {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 15)
  return `${timestamp}-${random}`.substring(0, 32)
}

const Kount360 = ({ clientId, environment }: Kount360Props) => {
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false)
  const setFraudSessionId = useFraudStore((state) => state.setFraud)

  useEffect(() => {
    if (hasAttemptedLoad) {
      return
    }

    try {
      // Generate a unique session ID for this device data collection
      const sessionID = generateSessionId()

      // Configure the Kount 360 SDK
      const kountConfig = {
        clientID: clientId,
        environment,
        isSinglePageApp: true,
      }

      // Set up a timeout to ensure we set the session ID even if collection doesn't complete
      let kountTimeout: number | NodeJS.Timeout

      // Invoke the SDK with callback configuration
      kountSDK(
        {
          ...kountConfig,
          callbacks: {
            'collect-begin': (params: {
              SessionID: string
              KountClientID: string
            }) => {
              // Get the iframe element if it exists
              const iframe = document.getElementById('ibody')

              // i18n fix: Add aria-label to iframe
              if (iframe) {
                iframe.setAttribute('aria-label', 'Anti-fraud verification')
              }

              // Set a timeout in case collection takes too long
              kountTimeout = setTimeout(() => {
                Logger.warn('Kount 360 device data collection timeout')
                setFraudSessionId(params.SessionID)
              }, 5000)
            },

            'collect-end': (params: {
              SessionID: string
              KountClientID: string
            }) => {
              clearTimeout(kountTimeout as number)
              // Set the session ID from the collected device data
              setFraudSessionId(params.SessionID)
            },
          },
        },
        sessionID,
      )

      setHasAttemptedLoad(true)
    } catch (error) {
      Logger.error('Error initializing Kount 360 device data collector:', error)
      setHasAttemptedLoad(true)
    }
  }, [hasAttemptedLoad, clientId, environment, setFraudSessionId])

  return undefined
}

export { Kount360 }
